import { useRef, useState } from 'react';
import { Upload, Trash2, Move, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const DEFAULT_TRANSFORM = { zoom: 1, x: 50, y: 50 };

export function ImageSlot({ image, slotIndex, pageIndex, onUpload, onRemove, caption, onCaptionChange, transform, onTransformChange }) {
  const inputRef = useRef(null);
  const [showAdjust, setShowAdjust] = useState(false);
  const tx = transform || DEFAULT_TRANSFORM;
  const cellRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onUpload(pageIndex, slotIndex, ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const updateTransform = (patch) => {
    if (onTransformChange) onTransformChange(pageIndex, slotIndex, { ...tx, ...patch });
  };

  // Drag to pan
  const startDrag = (e) => {
    if (!showAdjust || !cellRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = cellRef.current.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const startPx = tx.x;
    const startPy = tx.y;
    const onMove = (ev) => {
      const dxPercent = ((ev.clientX - startX) / rect.width) * 100;
      const dyPercent = ((ev.clientY - startY) / rect.height) * 100;
      const newX = Math.max(0, Math.min(100, startPx + dxPercent));
      const newY = Math.max(0, Math.min(100, startPy + dyPercent));
      updateTransform({ x: newX, y: newY });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Scale background-size based on zoom (1 = cover, 2 = 200%, etc.)
  const bgSize = `${tx.zoom * 100}%`;
  const bgPos = `${tx.x}% ${tx.y}%`;

  if (image) {
    return (
      <div className="relative w-full h-full group flex flex-col">
        <div
          ref={cellRef}
          className={`relative flex-1 min-h-0 overflow-hidden ${showAdjust ? 'cursor-move' : ''}`}
          onMouseDown={startDrag}
          data-testid={`slot-image-${pageIndex}-${slotIndex}`}
        >
          {/* Use background-image instead of <img> so we get free zoom + pan via CSS */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${image})`,
              backgroundSize: tx.zoom <= 1 ? 'cover' : bgSize,
              backgroundPosition: bgPos,
              backgroundRepeat: 'no-repeat',
            }}
          />
          {caption && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2 pointer-events-none">
              <p className="text-white text-xs font-medium text-center drop-shadow-md leading-tight">{caption}</p>
            </div>
          )}

          {/* Top-right action buttons */}
          <div className="absolute top-1.5 right-1.5 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={(e) => { e.stopPropagation(); setShowAdjust((v) => !v); }}
              className={`p-1.5 rounded-lg shadow-md ${showAdjust ? 'bg-[#2a9d8f] text-white' : 'bg-white text-slate-700'}`}
              title={showAdjust ? 'Stäng justering' : 'Justera bilden'}
              data-testid={`adjust-img-${pageIndex}-${slotIndex}`}
            >
              <Move className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRemove(pageIndex, slotIndex); }}
              className="p-1.5 bg-red-500/90 text-white rounded-lg shadow-md"
              data-testid={`remove-img-${pageIndex}-${slotIndex}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Adjust toolbar (visible when adjust mode is on) */}
          {showAdjust && (
            <div
              className="absolute bottom-1 left-1 right-1 bg-black/85 text-white rounded-md px-2 py-1.5 flex items-center justify-between gap-2 z-10"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <span className="text-[10px] font-medium uppercase tracking-wide opacity-70">Dra för att panorera</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => updateTransform({ zoom: Math.max(1, tx.zoom - 0.1) })}
                  className="p-1 hover:bg-white/15 rounded"
                  data-testid={`zoom-out-${pageIndex}-${slotIndex}`}
                  title="Zooma ut"
                >
                  <ZoomOut className="w-3.5 h-3.5" />
                </button>
                <span className="text-[10px] font-mono w-8 text-center tabular-nums">{Math.round(tx.zoom * 100)}%</span>
                <button
                  onClick={() => updateTransform({ zoom: Math.min(4, tx.zoom + 0.1) })}
                  className="p-1 hover:bg-white/15 rounded"
                  data-testid={`zoom-in-${pageIndex}-${slotIndex}`}
                  title="Zooma in"
                >
                  <ZoomIn className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => updateTransform(DEFAULT_TRANSFORM)}
                  className="p-1 hover:bg-white/15 rounded ml-1"
                  data-testid={`reset-img-${pageIndex}-${slotIndex}`}
                  title="Återställ"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        <input
          type="text"
          value={caption || ''}
          onChange={(e) => onCaptionChange(pageIndex, slotIndex, e.target.value)}
          placeholder="Skriv bildtext..."
          maxLength={80}
          className="w-full px-2 py-1.5 text-xs border-t border-slate-200 bg-slate-50 focus:outline-none focus:bg-white placeholder:text-slate-300"
          onClick={(e) => e.stopPropagation()}
          data-testid={`caption-${pageIndex}-${slotIndex}`}
        />
      </div>
    );
  }

  return (
    <div
      onClick={() => inputRef.current?.click()}
      className="w-full h-full flex flex-col items-center justify-center cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors border border-dashed border-slate-300 rounded"
      data-testid={`slot-${pageIndex}-${slotIndex}`}
    >
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      <Upload className="w-5 h-5 text-slate-400 mb-1" />
      <span className="text-xs text-slate-400">Lägg till bild</span>
    </div>
  );
}
